import React from "react";
import DateField from "../../fields/date_field";
import { borrower_names, truthy_join } from "../../../constants/utils";
import TITLE_VESTING from "../../../constants/title_vesting";
import TimeField from "../../fields/time_field";
import SelectField from "../../fields/select_field";
import ComboBox from "../../fields/combo_box";
import PercentField from "../../fields/percent_field";
import TextField from "../../fields/text_field";
import LENDERS from "../../../constants/lenders";
import { getAllLenders } from "../../../constants/custom_lenders";
import { useDocument } from "../../loan_tools/loan_tool";
import { useUser } from "../../../hooks/user";
import {
  loan_visible_for_viewer,
  is_wholesale_view_only
} from "../../../constants/lender_visibility";
import {
  LOSS_REASON_OPTIONS,
  LOSS_REASON_STATUSES,
  LOST_TO_COMPETION,
  LOST_TO_PRICING
} from "../../../constants/loan_status";

export default function LoanSelection({ updateSummary }) {
  const [
    { loans, summary, applicant, property },
    ,
    { documentAccountProfile }
  ] = useDocument();
  const [{ accountProfile }] = useUser();
  const borrowerNames = borrower_names(applicant.borrowers);
  // Only offer the viewer's own lender's loans in the selection dropdown (keeps
  // the loan number as the value).
  const loanSelections = loans.reduce((selections, loan, index) => {
    if (loan_visible_for_viewer(loan, accountProfile)) {
      selections[String(index + 1)] = String(index + 1);
    }
    return selections;
  }, {});
  // Losing scenarios: once the borrower selects the winning loan, the LO/broker
  // records WHY each non-selected, lender-assigned option was lost. This is
  // INTERNAL management data (never printed, never shown to the borrower); it's
  // what fans out to the competing lenders' AEs as a Lost row in VISION. Only a
  // loan with a Lender assigned is a real scenario an AE could see.
  const selectedNumber = summary.selection_loan_number;
  // The winner's lender. A "loss" only exists when a competing scenario went to a
  // DIFFERENT lender — if every priced option is the same lender (e.g. all Kind
  // Lending), the borrower simply chose one option and there's no win/loss to
  // record. Same-lender alternates are not losses either.
  const winnerLender = selectedNumber
    ? (
        ((loans[Number(selectedNumber) - 1] || {}).lender) || ""
      )
        .trim()
        .toLowerCase()
    : "";
  const losingScenarios = selectedNumber
    ? loans.reduce((rows, loan, index) => {
        const loanNumber = String(index + 1);
        const lender = ((loan && loan.lender) || "").trim();
        if (
          lender &&
          loanNumber !== String(selectedNumber) &&
          lender.toLowerCase() !== winnerLender
        ) {
          rows.push({ loanNumber, lender });
        }
        return rows;
      }, [])
    : [];
  // Only the file owner (retail/broker LO) records the loss reasons; a wholesale
  // AE sees a filtered, view-only sheet and never sets competitors' outcomes.
  const isFileOwner = !is_wholesale_view_only(accountProfile);

  const loanOfficerName = truthy_join(
    [
      documentAccountProfile.first_name,
      documentAccountProfile.middle_name,
      documentAccountProfile.last_name
    ],
    " "
  );

  return (
    <>
      <tbody>
        <tr>
          <td colSpan="8">
            <div>
              Appraisal fees will not be able to be charged, nor credit card
              information collected, prior to borrower confirmation of the
              intent to proceed with the loan after issuance of the Loan
              Estimate, which must be delivered to the consumer within three
              days of application.
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8" />
        </tr>
        <tr>
          <td colSpan="8">
            <div>
              Please indicate which loan you intend to Proceed with. Once you
              have selected the appropriate Loan, we will issue a formal Loan
              Estimate and Loan Disclosures.
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8" />
        </tr>
        <tr>
          <td colSpan="8">
            <div className="no-print loan-selection flex-space-between">
              <SelectField
                emptyDefault
                showDefaultOption
                name="I / We select Loan Number:"
                fieldName="selection_loan_number"
                value={summary.selection_loan_number}
                selectOptions={loanSelections}
                onChange={updateSummary}
              />
              <DateField
                name="Date:"
                fieldName="selection_date"
                value={summary.selection_date}
                onChange={updateSummary}
              />
              <TimeField
                name="Time:"
                fieldName="selection_time"
                value={summary.selection_time}
                onChange={updateSummary}
              />
            </div>
            <div className="print-only-flex loan-selection flex-space-between">
              <div>
                <span>I / We select Loan Number:</span>
                <span className="printable gray-bg">
                  &nbsp;
                  <SelectField
                    emptyDefault
                    naked
                    value={summary.selection_loan_number}
                    selectOptions={loanSelections}
                  />
                  &nbsp;
                </span>
              </div>
              <div>
                <span>Date:</span>
                <span className="printable gray-bg">
                  &nbsp;
                  <DateField naked value={summary.selection_date} />
                  &nbsp;
                </span>
              </div>
              <div>
                <span>Time:</span>
                <span className="printable gray-bg">
                  &nbsp;
                  <TimeField naked value={summary.selection_time} />
                  &nbsp;
                </span>
              </div>
            </div>
          </td>
        </tr>
        {isFileOwner && losingScenarios.length > 0 && (
          <tr className="no-print">
            <td colSpan="8">
              <div className="loss-reason-block">
                <div className="loss-reason-title">
                  Loss reason for competing loan options (internal — not printed,
                  not shown to the borrower):
                </div>
                {losingScenarios.map(scenario => {
                  const reason =
                    summary["loss_reason_loan_" + scenario.loanNumber];
                  // Competitive intel is captured only when the loan was lost to
                  // someone else — the LO/AE enters where it went and at what
                  // price (LANIS can't derive a competitor's pricing).
                  const isCompetitive =
                    reason === LOST_TO_COMPETION || reason === LOST_TO_PRICING;
                  return (
                    <div className="loss-reason-row" key={scenario.loanNumber}>
                      <SelectField
                        emptyDefault
                        showDefaultOption
                        name={
                          "Loan " +
                          scenario.loanNumber +
                          " — " +
                          scenario.lender +
                          ":"
                        }
                        fieldName={"loss_reason_loan_" + scenario.loanNumber}
                        value={reason}
                        selectOptions={LOSS_REASON_OPTIONS}
                        optionOrder={LOSS_REASON_STATUSES}
                        onChange={updateSummary}
                      />
                      {isCompetitive && (
                        <div className="loss-competitor">
                          <ComboBox
                            name="Lost to (company):"
                            fieldName={
                              "lost_to_competitor_loan_" + scenario.loanNumber
                            }
                            value={
                              summary[
                                "lost_to_competitor_loan_" + scenario.loanNumber
                              ]
                            }
                            options={getAllLenders(LENDERS)}
                            placeholder="Company / lender the loan went to"
                            onChange={updateSummary}
                          />
                          <PercentField
                            name="Their rate:"
                            fieldName={
                              "lost_to_rate_loan_" + scenario.loanNumber
                            }
                            value={
                              summary["lost_to_rate_loan_" + scenario.loanNumber]
                            }
                            onChange={updateSummary}
                          />
                          <TextField
                            name="Their price / points / note:"
                            fieldName={
                              "lost_to_note_loan_" + scenario.loanNumber
                            }
                            value={
                              summary["lost_to_note_loan_" + scenario.loanNumber]
                            }
                            placeholder="e.g. -0.5 pts, no origination, 45-day lock"
                            onChange={updateSummary}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </td>
          </tr>
        )}
        <tr>
          <td colSpan="8" />
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-line" />
              <div className="signature-line" />
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-block">
                <div>Borrower</div>
                <div>Date</div>
              </div>
              <div className="signature-block">
                <div>Borrower</div>
                <div>Date</div>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-block">
                <div>{borrowerNames[0]}</div>
              </div>
              <div className="signature-block">
                <div>{borrowerNames[1]}</div>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8" />
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-line" />
              <div className="signature-line" />
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-block">
                <div>Borrower</div>
                <div>Date</div>
              </div>
              <div className="signature-block">
                <div>Borrower</div>
                <div>Date</div>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-block">
                <div>{borrowerNames[2]}</div>
              </div>
              <div className="signature-block">
                <div>{borrowerNames[3]}</div>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          {/* Formatted this way to match the row below due to chrome border bug */}
          <td colSpan="3" />
          <td colSpan="3" />
          <td colSpan="2" />
        </tr>
        <tr>
          <td className="with-border" colSpan="3">
            Manner in which title will be held.
          </td>
          <td className="with-border" colSpan="3">
            {TITLE_VESTING[property.title_vesting]}
          </td>
          <td colSpan="2" />
        </tr>
        <tr>
          {/* Formatted this way to match the row below due to chrome border bug */}
          <td colSpan="3" />
          <td colSpan="3" />
          <td colSpan="2" />
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-line" />
              <div className="signature-line" />
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-block">
                <div>{loanOfficerName}</div>
                <div>Date</div>
              </div>
              <div className="signature-block">
                <div>{loanOfficerName}</div>
                <div>Date</div>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td colSpan="8">
            <div className="signature-container">
              <div className="signature-block">
                <div>Loan Officer</div>
              </div>
              <div className="signature-block">
                <div>Broker</div>
              </div>
            </div>
          </td>
        </tr>
        <tr>
          <td className="gray-bg" colSpan="8" />
        </tr>
      </tbody>
    </>
  );
}
